import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaLink, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Import gallery images
// AI-themed images - Add these to your assets folder
// import ai1 from '../../assets/images/gallery/ai/ai-robotics-lab.jpg';
// import ai2 from '../../assets/images/gallery/ai/ai-students-learning.jpg';
// import ai3 from '../../assets/images/gallery/ai/ai-stem-future.jpg';
// import ai4 from '../../assets/images/gallery/ai/ai-innovation-hub.jpg';

// Course images
import course1 from '../../assets/images/gallery/courses/1.jpg';
import course2 from '../../assets/images/gallery/courses/2.jpg';
import course3 from '../../assets/images/gallery/courses/3.jpg';
import course4 from '../../assets/images/gallery/courses/4.jpg';

// Workshop images
import workshop1 from '../../assets/images/gallery/workshops/1.jpg';
import workshop2 from '../../assets/images/gallery/workshops/2.jpg';
import workshop3 from '../../assets/images/gallery/workshops/3.jpg';
import workshop4 from '../../assets/images/gallery/workshops/4.jpg';

// Event images
import event1 from '../../assets/images/gallery/events/1.jpg';
import event2 from '../../assets/images/gallery/events/2.jpg';
import event3 from '../../assets/images/gallery/events/3.jpg';
import event4 from '../../assets/images/gallery/events/4.jpg';

// Gallery images by category
const galleryItems = [
  // AI & Future Tech
  // {
  //   id: 101,
  //   title: 'AI Robotics Laboratory',
  //   category: 'ai',
  //   image: ai1
  // },
  // {
  //   id: 102,
  //   title: 'Next-Gen AI Learning Experience',
  //   category: 'ai',
  //   image: ai2
  // },
  // {
  //   id: 103,
  //   title: 'Shaping the Future with AI & STEM',
  //   category: 'ai',
  //   image: ai3
  // },
  // {
  //   id: 104,
  //   title: 'Innovation Hub: Where AI Meets Education',
  //   category: 'ai',
  //   image: ai4
  // },
  
  // Courses
  {
    id: 1,
    title: 'Robotics Course Session',
    category: 'courses',
    image: course1
  },
  {
    id: 2,
    title: 'STEM Education Workshop',
    category: 'courses',
    image: course2
  },
  {
    id: 3,
    title: 'Programming Class',
    category: 'courses',
    image: course3
  },
  {
    id: 4,
    title: 'Electronics Tutorial',
    category: 'courses',
    image: course4
  },
  
  // Workshops
  {
    id: 5,
    title: 'School Workshop',
    category: 'workshops',
    image: workshop1
  },
  {
    id: 6,
    title: 'Hands-on Electronics',
    category: 'workshops',
    image: workshop2
  },
  {
    id: 7,
    title: 'Group Project Session',
    category: 'workshops',
    image: workshop3
  },
  {
    id: 8,
    title: 'Robotics Workshop',
    category: 'workshops',
    image: workshop4
  },
  
  // Events
  {
    id: 9,
    title: 'Competition Day',
    category: 'events',
    image: event1
  },
  {
    id: 10,
    title: 'Exhibition Booth',
    category: 'events',
    image: event2
  },
  {
    id: 11,
    title: 'Award Ceremony',
    category: 'events',
    image: event3
  },
  {
    id: 12,
    title: 'Robotics Challenge',
    category: 'events',
    image: event4
  }
];

// Lightbox component for viewing images in a larger format
const ImageLightbox = ({ isOpen, onClose, item, onNext, onPrevious, currentIndex, totalItems }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') {
      onPrevious();
    } else if (e.key === 'ArrowRight') {
      onNext();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [onNext, onPrevious, onClose]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !item) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div 
          className="relative max-w-4xl max-h-[90vh] w-full mx-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            className="absolute -top-12 right-0 text-white hover:text-theme-accent focus:outline-none z-20"
            onClick={onClose}
          >
            <FaTimes size={24} />
          </button>
          
          {/* Navigation buttons */}
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-2 rounded-full z-20 transition-all duration-300 transform hover:scale-110"
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
          >
            <FaChevronLeft size={20} />
          </button>
          
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-2 rounded-full z-20 transition-all duration-300 transform hover:scale-110"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            <FaChevronRight size={20} />
          </button>
          
          <div className="relative w-full h-full overflow-hidden rounded-lg shadow-2xl">
            <motion.img 
              key={item.id}
              src={item.image} 
              alt={item.title} 
              className="w-full h-full object-contain image-enhance"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 p-4 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-sm text-gray-300 mt-1">Category: {item.category.charAt(0).toUpperCase() + item.category.slice(1)}</p>
                </div>
                <div className="text-sm text-gray-300">
                  {currentIndex + 1} / {totalItems}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const GallerySection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredItems, setFilteredItems] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Update filtered items when activeFilter changes
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredItems(galleryItems);
    } else {
      const filtered = galleryItems.filter(item => item.category === activeFilter);
      setFilteredItems(filtered);
    }
  }, [activeFilter]);
  
  // Set filter
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };
  
  // Open lightbox
  const openLightbox = (item) => {
    setSelectedItem(item);
    const index = filteredItems.findIndex(i => i.id === item.id);
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  
  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  // Navigate to next image
  const handleNext = useCallback(() => {
    if (filteredItems.length <= 1) return;
    const newIndex = (currentIndex + 1) % filteredItems.length;
    setCurrentIndex(newIndex);
    setSelectedItem(filteredItems[newIndex]);
  }, [currentIndex, filteredItems]);

  // Navigate to previous image
  const handlePrevious = useCallback(() => {
    if (filteredItems.length <= 1) return;
    const newIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    setCurrentIndex(newIndex);
    setSelectedItem(filteredItems[newIndex]);
  }, [currentIndex, filteredItems]);

  // Custom CSS for image enhancement
  const imageEnhanceStyle = `
    .image-enhance {
      filter: contrast(1.05) saturate(1.1);
      object-fit: cover;
      transform: scale(1);
      transition: transform 0.5s ease;
      image-rendering: -webkit-optimize-contrast;
      backface-visibility: hidden;
    }
    
    .image-enhance:hover {
      transform: scale(1.03);
    }
    
    .gallery-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle, rgba(0,0,0,0) 60%, rgba(0,0,0,0.15) 100%);
      z-index: 1;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }
    
    .gallery-card:hover::before {
      opacity: 1;
    }
  `;
  
  return (
    <section id="gallery" className="py-16 bg-gray-100">
      <style>{imageEnhanceStyle}</style>
      <div className="container mx-auto px-4">
        {/* Gallery Filter */}
        <div className="flex flex-wrap justify-center mb-8 space-x-2">
          <button 
            onClick={() => handleFilterClick('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === 'all' 
                ? 'bg-theme-blue text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button 
            onClick={() => handleFilterClick('ai')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === 'ai' 
                ? 'bg-theme-blue text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            AI & Future Tech
          </button>
          <button 
            onClick={() => handleFilterClick('courses')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === 'courses' 
                ? 'bg-theme-blue text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            Courses
          </button>
          <button 
            onClick={() => handleFilterClick('workshops')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === 'workshops' 
                ? 'bg-theme-blue text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            Workshops
          </button>
          <button 
            onClick={() => handleFilterClick('events')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeFilter === 'events' 
                ? 'bg-theme-blue text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-200'
            }`}
          >
            Events
          </button>
        </div>
        
        {/* Gallery Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          <AnimatePresence>
            {filteredItems.map((item) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                key={item.id}
                className="overflow-hidden rounded-lg shadow-md bg-white gallery-card relative"
              >
                <div className="group relative overflow-hidden">
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover image-enhance"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="text-center px-4">
                      <div className="flex space-x-4 mb-2 justify-center">
                        <button 
                          className="w-10 h-10 rounded-full bg-theme-accent flex items-center justify-center text-white hover:bg-theme-blue transition-colors transform hover:scale-110 duration-300"
                          onClick={() => openLightbox(item)}
                        >
                          <FaSearch size={16} />
                        </button>
                        <a 
                          href="#" 
                          className="w-10 h-10 rounded-full bg-theme-accent flex items-center justify-center text-white hover:bg-theme-blue transition-colors transform hover:scale-110 duration-300"
                        >
                          <FaLink size={16} />
                        </a>
                      </div>
                      <h4 className="text-white font-bold">{item.title}</h4>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      
      {/* Lightbox */}
      <ImageLightbox 
        isOpen={lightboxOpen} 
        onClose={closeLightbox} 
        item={selectedItem} 
        onNext={handleNext}
        onPrevious={handlePrevious}
        currentIndex={currentIndex}
        totalItems={filteredItems.length}
      />
    </section>
  );
};

export default GallerySection; 